import groq from "groq";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type BlogSimple, blogSimpleFragment } from "./blogs";
import { type Customer, customerFragment } from "./customers";

export type Banner = {
  title: string;
  description: string;
  ctaLink: string;
};

type SiteSettings = {
  announcementBanner?: Banner;
  defaultFeaturedBlogs?: BlogSimple[];
  b2cLogos?: Customer[];
  b2bLogos?: Customer[];
};

export const getAnnouncementBanner = async (): Promise<Banner | undefined> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      announcementBanner->{title, description, ctaLink},
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.announcementBanner;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch announcement banner: ${errorMessage}`);
    return undefined;
  }
};

export const getDefaultBlogs = async (): Promise<BlogSimple[]> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      defaultFeaturedBlogs[]->{ ${blogSimpleFragment} },
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.defaultFeaturedBlogs ?? [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch default blogs: ${errorMessage}`);
    return [];
  }
};

export const getDefaultLogos = async (): Promise<Customer[]> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      b2cLogos[]->{ ${customerFragment} },
      b2bLogos[]->{ ${customerFragment} },
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    const b2cLogos = siteSettings?.b2cLogos ?? [];
    const b2bLogos = siteSettings?.b2bLogos ?? [];
    return [...b2cLogos, ...b2bLogos];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch default customer logos from site settings: ${errorMessage}`);
    return [];
  }
};
